import React, { useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@linaria/react";
import { Body, PreTitle, TitleMed } from "../typography";
import useIsMobile from "hooks/useIsMobile";
import SliderNavDots from "../slider-components/SliderNavDots";
import { useSwipeable } from "react-swipeable";
import PageSection from "components/rui/page-components/PageSection";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { css } from "@linaria/core";
import { useSpring, animated } from "@react-spring/web";

const TabberContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--section-gap);
  width: 100%;
  align-items: center;
  @media (max-width: 900px) {
    gap: var(--section-gap-mobile);
  }
`;

const TabberBar = styled.div`
  position: relative;
  display: flex;
  border-radius: 4px;
  border: 1px solid var(--blue-500);
  user-select: none;
  overflow: hidden;
  width: 100%;
  max-width: fit-content;
  @media (max-width: 900px) {
    flex-wrap: wrap;
  }
`;

const AnimatedHighlight = styled(animated.div)`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background-color: var(--blue-500);
  z-index: 0;
  @media (max-width: 900px) {
    display: none;
  }
`;

const Tab = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--headerFontFamily);
  text-align: center;
  padding: 12px 40px;
  cursor: pointer;
  transition: all 250ms;
  font-size: 20px;
  color: ${(props) => (props.active ? "var(--white)" : "var(--body-subtle)")};
  background-color: ${(props) => (props.active ? "var(--blue-500)" : "unset")};
  :not(:last-of-type) {
    border-right: 1px solid var(--blue-500);
  }
  :hover {
    background-color: var(--blue-500);
    color: var(--white);
  }
  @media (max-width: 900px) {
    width: 50%;
    height: 60px;
    font-size: 16px;
    :nth-child(3) {
      border-right: none;
    }
    :nth-child(2),
    :nth-child(3) {
      border-bottom: 1px solid var(--blue-500);
    }
  }
  @media (max-width: 500px) {
    font-size: 14px;
  }
  @media (max-width: 400px) {
    font-size: 12px;
  }
`;

const TabContentContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--section-gap-mobile);
  width: 100%;
  @media (max-width: 900px) {
    flex-direction: column-reverse;
    gap: calc(var(--section-gap-mobile) / 2);
  }
`;
const TabContentLeft = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  gap: 24px;
  min-height: 300px;
  @media (max-width: 900px) {
    width: 100%;
    min-height: unset;
  }
`;

const TabContentRight = styled.div`
  width: 50%;
  @media (max-width: 900px) {
    width: 100%;
  }
`;

const imageStyles = css`
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
`;

const CONTENT_MODEL = [
  { tabTitle: "", preTitle: "", title: "", blurb: "", imageData: "", alt: "" },
];

const TabberSection = ({ sectionTitle, tabContent = CONTENT_MODEL }) => {
  const { isMobile } = useIsMobile(900);
  const [currentIndex, setCurrentIndex] = useState(0);
  const tabRefs = useRef([]);

  const [highlightStyle, api] = useSpring(() => ({
    left: 0,
    width: 0,
    config: {
      tension: 120,
      friction: 14,
      clamp: true,
    },
  }));

  useEffect(() => {
    if (tabRefs.current[currentIndex]) {
      const tabEl = tabRefs.current[currentIndex];
      const { offsetLeft, offsetWidth } = tabEl;

      api.start({
        left: offsetLeft,
        width: offsetWidth,
      });
    }
  }, [currentIndex, api]);

  const tabs = useMemo(() => {
    return tabContent.map((t) => t.tabTitle);
  }, [tabContent]);

  const currentTabContent = useMemo(() => {
    return tabContent[currentIndex];
  }, [currentIndex, tabContent]);

  const handlers = useSwipeable({
    onSwipedLeft: () => nextSlide(),
    onSwipedRight: () => prevSlide(),
    trackMouse: true,
  });

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % tabs.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? tabs.length - 1 : prevIndex - 1
    );
  };

  return (
    <PageSection title={sectionTitle}>
      <TabberContainer>
        <TabberBar>
          <AnimatedHighlight style={highlightStyle} />
          {tabs.map((t, index) => (
            <Tab
              key={index}
              ref={(el) => (tabRefs.current[index] = el)}
              onClick={() => setCurrentIndex(index)}
              active={index === currentIndex}
              style={{ position: "relative", zIndex: 1 }} // lift above the highlight
            >
              {t}
            </Tab>
          ))}
        </TabberBar>
        <TabContentContainer {...handlers}>
          <TabContentLeft>
            <PreTitle color="var(--primary-bg)">
              {currentTabContent.preTitle}
            </PreTitle>
            <TitleMed>{currentTabContent.title}</TitleMed>
            <Body>{currentTabContent.blurb}</Body>
          </TabContentLeft>
          <TabContentRight>
            <GatsbyImage
              image={getImage(currentTabContent.imageData)}
              alt={currentTabContent.alt}
              loading="lazy"
              className={imageStyles}
            />
          </TabContentRight>
        </TabContentContainer>
        {isMobile && (
          <SliderNavDots
            currentIndex={currentIndex}
            setCurrentIndex={setCurrentIndex}
            slides={tabs}
          />
        )}
      </TabberContainer>
    </PageSection>
  );
};

export default TabberSection;
